import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import {
  useFlags,
  usePassEquip,
  usePassSeen,
  usePassWardrobe,
  useQualification,
} from '../api/hooks';
import { ErrorView, Loader } from '../components/StateViews';
import { CosmeticAvatar } from '../components/cosmetics/CosmeticAvatar';
import { BackIcon } from '../components/icons';
import { SlotGlyph } from '../components/pass/RewardPreview';
import {
  assetUrl,
  RARITY_COLOR,
  RARITY_LABEL_M,
  SLOT_LABEL,
  rarityTint,
} from '../components/cosmetics/cosmeticsMap';
import { NickFx } from '../components/cosmetics/NickFx';
import { ProfileBg } from '../components/cosmetics/ProfileBg';
import { RowFxLayer, rowFxClass } from '../components/cosmetics/RowFx';
import { LottieLayer } from '../components/cosmetics/LottieLayer';
import { backButton, haptics } from '../telegram';
import { track } from '../analytics';
import type { CosmeticItem, PassWardrobe } from '../api/types';

type Slot = 'frame' | 'nick' | 'row' | 'bg';
const SLOTS: Slot[] = ['frame', 'nick', 'row', 'bg'];
type WItem = PassWardrobe['items'][number];

const asCos = (i: WItem | null | undefined): CosmeticItem | null =>
  i ? { code: i.code, kind: i.kind, asset: i.asset, rarity: i.rarity } : null;

function EyeIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
      <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z" />
      <circle cx="12" cy="12" r="2.6" />
    </svg>
  );
}

const SLOT_ICON: Record<Slot, JSX.Element> = {
  frame: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.5" />
    </svg>
  ),
  nick: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden>
      <path d="M5 18 12 5l7 13M7.5 14h9" />
    </svg>
  ),
  row: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden>
      <rect x="3" y="8" width="18" height="8" rx="3" />
      <circle cx="8" cy="12" r="1.6" fill="currentColor" stroke="none" />
      <path d="M12 12h6" />
    </svg>
  ),
  bg: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="M4 15l4.5-4.5L14 16M13 12l3-3 4 4" />
    </svg>
  ),
};

/** 6.7 — гардероб по Handoff 3: превью «как меня видят» с примеркой,
 *  табы-плитки слотов, фильтр редкости, сетка с «по умолчанию» и «?»,
 *  нижняя панель примерки. «Новое» снимается при первом открытии слота. */
export function WardrobeScreen() {
  const flags = useFlags();
  const enabled = Boolean(flags.data?.pass);
  const wardrobe = usePassWardrobe(enabled);
  const qual = useQualification();
  const equip = usePassEquip();
  const seen = usePassSeen();
  const { player } = useAuth();
  const navigate = useNavigate();
  const [slot, setSlot] = useState<Slot>('frame');
  const [onlyNew, setOnlyNew] = useState(false);
  const [onlyOwned, setOnlyOwned] = useState(false);
  const [rarityFilter, setRarityFilter] = useState<number | null>(null);
  const [tryOn, setTryOn] = useState<WItem | null>(null);
  const [tryDefault, setTryDefault] = useState<Slot | null>(null);

  useEffect(() => {
    // Явный путь: в истории может лежать /preview (6.7b) — «назад» зацикливался.
    const back = () => navigate('/profile');
    backButton.show(back);
    return () => backButton.hide(back);
  }, [navigate]);

  // «Новое»: на входе снимаем серверный флаг разом (иначе бейдж возвращался
  // на не открытых слотах), а на время сессии держим снапшот новых id —
  // фильтр и бейджи живут от него. Просмотр/надевание предмета гасит его
  // бейдж сразу (markViewed), поэтому снапшот — state, не ref.
  const [freshSet, setFreshSet] = useState<Set<number> | null>(null);
  const acked = useRef(false);
  useEffect(() => {
    if (!wardrobe.data) return;
    if (freshSet === null) {
      setFreshSet(new Set(wardrobe.data.items.filter((i) => i.isNew).map((i) => i.id)));
    }
    if (!acked.current && wardrobe.data.items.some((i) => i.isNew)) {
      acked.current = true;
      void seen.mutateAsync(undefined).catch(() => undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wardrobe.data]);
  const isFresh = (i: { id: number; isNew: boolean }) =>
    freshSet ? freshSet.has(i.id) : i.isNew;
  /** Предмет посмотрели (тап/надели) — «Новое» на нём гаснет. */
  const markViewed = (id: number) =>
    setFreshSet((prev) => {
      if (!prev || !prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

  // Если новых нет — фильтр «Новое» не должен запирать пустую сетку.
  useEffect(() => {
    if (!onlyNew || !wardrobe.data) return;
    if (!wardrobe.data.items.some((i) => isFresh(i))) setOnlyNew(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyNew, wardrobe.data, freshSet]);

  const pickSlot = (s: Slot) => {
    haptics.tap();
    setSlot(s);
    setTryOn(null); // переключение таба сбрасывает примерку
    setTryDefault(null);
  };

  const equippedOf = (s: Slot): WItem | null => {
    const id = wardrobe.data?.equipped[s] ?? null;
    return wardrobe.data?.items.find((i) => i.id === id) ?? null;
  };
  const shown = useMemo(() => {
    const items = wardrobe.data?.items ?? [];
    return items.filter(
      (i) =>
        i.slot === slot &&
        (!onlyNew || isFresh(i)) &&
        (!onlyOwned || i.owned) &&
        (rarityFilter === null || i.rarity === rarityFilter),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wardrobe.data, slot, onlyNew, onlyOwned, rarityFilter, freshSet]);

  if (flags.data && !enabled) return <Navigate to="/" replace />;
  if (!flags.data) return <Loader />;
  if (wardrobe.isLoading) return <Loader />;
  if (wardrobe.isError || !wardrobe.data) {
    return <ErrorView message={(wardrobe.error as Error)?.message} />;
  }
  const items = wardrobe.data.items;
  const ownedCount = items.filter((i) => i.owned).length;
  const newTotal = items.filter((i) => isFresh(i)).length;
  const newCount = (s: Slot) => items.filter((i) => i.slot === s && isFresh(i)).length;
  const name = player?.displayName ?? 'Игрок';

  // Превью с учётом примерки (примерка «по умолчанию» — слот пустой).
  const pv = (s: Slot): WItem | null =>
    tryDefault === s ? null : tryOn?.slot === s ? tryOn : equippedOf(s);
  const trying = tryOn !== null || tryDefault !== null;
  // Выбран уже надетый предмет — это просмотр, а не примерка.
  const selEquipped =
    tryDefault === null && tryOn !== null && wardrobe.data?.equipped[tryOn.slot as Slot] === tryOn.id;
  const me =
    qual.data?.status === 'active' || qual.data?.status === 'finished' ? qual.data.me : null;

  const doEquip = (s: Slot, itemId: number | null) => {
    haptics.tap();
    void equip
      .mutateAsync({ slot: s, itemId })
      .then(() => {
        haptics.success();
        if (itemId !== null) markViewed(itemId);
        setTryOn(null);
        setTryDefault(null);
      })
      .catch(() => haptics.error());
  };

  return (
    <div className="bp-screen bp-wr">
      <div className="subhead">
        <button className="iconbtn" onClick={() => navigate('/profile')} aria-label="Назад">
          <BackIcon />
        </button>
        <div className="title">Гардероб</div>
        <span className="bp-wr-head-right">
          {newTotal > 0 && (
            <button
              className={`bp-wr-newchip${onlyNew ? ' on' : ''}`}
              onClick={() => {
                haptics.tap();
                // макет: тап переключает и на первый слот, где есть новые
                if (!onlyNew && newCount(slot) === 0) {
                  const first = SLOTS.find((s) => newCount(s) > 0);
                  if (first) pickSlot(first);
                }
                setOnlyNew((v) => !v);
              }}
            >
              <i />
              Новое · {newTotal}
            </button>
          )}
          <span className="bp-wr-count mono">
            {ownedCount} / {items.length}
          </span>
        </span>
      </div>

      {/* Превью «как меня видят» (116px, фон с эффектом, градиент снизу) */}
      <div className={`bp-wr-preview pbg-host${trying && !selEquipped ? ' bp-wr-preview--try' : ''}`} data-theme="dark">
        <ProfileBg cosmetic={asCos(pv('bg'))} />
        <span className="bp-wr-grad" aria-hidden />
        {trying && !selEquipped && (
          <span className="bp-wr-trybadge">
            <EyeIcon size={11} /> Примерка
          </span>
        )}
        <div className="bp-wr-prevmain">
          <CosmeticAvatar name={name} src={player?.photoUrl} size={58} frame={asCos(pv('frame'))} />
          <div className="bp-wr-previnfo">
            <div className="bp-wr-nick">
              <NickFx cosmetic={asCos(pv('nick'))}>{name}</NickFx>
            </div>
            <div className="bp-wr-standing">
              {me && me.rank !== null ? `#${me.rank} в отборе · ${me.rating}` : 'Отбор впереди'}
            </div>
          </div>
        </div>
        {/* Мини-строка рейтинга как в 6.7: место · имя · баллы с эффектом строки */}
      </div>

      {/* Табы слотов — 4 плитки с иконками и бейджем новых */}
      <div className="bp-wr-slots">
        {SLOTS.map((s) => (
          <button key={s} className={`bp-wr-slot${slot === s ? ' on' : ''}`} onClick={() => pickSlot(s)}>
            {newCount(s) > 0 && <b className="bp-wr-slotnew">{newCount(s)}</b>}
            {SLOT_ICON[s]}
            <span>{SLOT_LABEL[s]}</span>
          </button>
        ))}
      </div>

      {/* Фильтр редкости с ромбами — короткие подписи (макет 6.7) */}
      <div className="bp-wr-filters">
        <button
          className={`bp-wr-filter${rarityFilter === null ? ' on' : ''}`}
          onClick={() => setRarityFilter(null)}
        >
          Все
        </button>
        <button
          className={`bp-wr-filter${onlyOwned ? ' on' : ''}`}
          onClick={() => {
            haptics.tap();
            setOnlyOwned((v) => !v);
          }}
        >
          В наличии
        </button>
        {[0, 1, 2, 3].map((r) => (
          <button
            key={r}
            className={`bp-wr-filter${rarityFilter === r ? ' on' : ''}`}
            onClick={() => setRarityFilter(rarityFilter === r ? null : r)}
          >
            <i className="bp-wr-pip" style={{ background: RARITY_COLOR[r] }} />
            {['Об.', 'Ред.', 'Эпик', 'Лег.'][r]}
          </button>
        ))}
      </div>

      <div className="bp-wr-grid bp-fade-swap" key={`${slot}-${rarityFilter}-${onlyNew}-${onlyOwned}`}>
        {/* «По умолчанию» — стандартный вариант слота, ромбов нет */}
        {rarityFilter === null && !onlyNew && (
          <button
            className={`bp-wr-card${wardrobe.data.equipped[slot] === null && !trying ? ' bp-wr-card--on' : ''}${tryDefault === slot ? ' bp-wr-card--try' : ''}`}
            onClick={() => {
              haptics.tap();
              if (tryDefault === slot) {
                setTryDefault(null);
                return;
              }
              setTryOn(null);
              setTryDefault(slot);
            }}
          >
            <span className="bp-wr-thumb">
              <DefaultThumb slot={slot} name={name} />
            </span>
            {/* макет: «Без цвета»/«Без эффекта»; подпись по состоянию */}
            <span className="nm">{slot === 'nick' ? 'Без цвета' : slot === 'row' ? 'Без эффекта' : 'Стандарт'}</span>
            <span className="bp-wr-meta">
              <span className="sub">
                {tryDefault === slot
                  ? 'примерка'
                  : wardrobe.data.equipped[slot] === null
                    ? 'надето'
                    : 'по умолчанию'}
              </span>
            </span>
            {wardrobe.data.equipped[slot] === null && <EquippedTick />}
          </button>
        )}
        {shown.map((i) => {
          const isEquipped = wardrobe.data.equipped[i.slot as Slot] === i.id;
          const isTrying = tryOn?.id === i.id;
          return (
            <button
              key={i.id}
              className={`bp-wr-card${isEquipped ? ' bp-wr-card--on' : ''}${isTrying ? ' bp-wr-card--try' : ''}${!i.owned ? ' bp-wr-card--ghost' : ''}`}
              style={i.owned && i.rarity >= 2 && !isEquipped && !isTrying ? { borderColor: rarityTint(i.rarity, 0.55) } : undefined}
              onClick={() => {
                // тап по надетому — выбор с кнопкой «Как меня видят другие»
                haptics.tap();
                track('pass_wardrobe_tap', { item: i.code, owned: i.owned });
                markViewed(i.id);
                setTryDefault(null);
                setTryOn(isTrying ? null : i);
              }}
            >
              <span className="bp-wr-thumb" style={{ color: RARITY_COLOR[i.rarity] }}>
                <ItemThumb item={i} name={name} />
                {!i.owned && <span className="bp-wr-q mono">?</span>}
              </span>
              <span className={`nm${!i.owned ? ' nm--ghost' : ''}`}>{i.name}</span>
              <span className="bp-wr-meta">
                <span className="pips" style={{ color: RARITY_COLOR[i.rarity] }}>
                  {Array.from({ length: i.rarity + 1 }, (_, k) => (
                    <i key={k} />
                  ))}
                </span>
                <span className="sub">
                  {isEquipped
                    ? 'надето'
                    : isTrying
                      ? 'примерка'
                      : i.owned
                        ? RARITY_LABEL_M[i.rarity]
                        : fromLabel(i)}
                </span>
              </span>
              {isEquipped && <EquippedTick />}
              {isFresh(i) && <span className="bp-wr-newbadge">Новое</span>}
            </button>
          );
        })}
        {shown.length === 0 && (rarityFilter !== null || onlyNew || onlyOwned) && (
          <div className="q-empty">Здесь пока пусто</div>
        )}
      </div>

      {/* Нижняя панель: примерка или «Как меня видят другие» */}
      <div className="bp-wr-bottom">
        {trying ? (
          <>
            <div className="bp-wr-tryname">
              {selEquipped ? `Надето: ${tryOn?.name}` : `Примерка: ${tryDefault ? 'Стандарт' : tryOn?.name}`}
            </div>
            <div className="bp-wr-tryrow">
              <button
                className="bp-wr-cancel"
                onClick={() => {
                  haptics.tap();
                  setTryOn(null);
                  setTryDefault(null);
                }}
              >
                Отмена
              </button>
              {selEquipped ? (
                // выбран надетый предмет — сразу можно посмотреть на себя
                <button
                  className="btn bp-wr-others bp-wr-equip"
                  onClick={() => {
                    haptics.tap();
                    navigate('/pass/wardrobe/preview');
                  }}
                >
                  <EyeIcon /> Как меня видят другие
                </button>
              ) : tryDefault !== null || tryOn?.owned ? (
                <button
                  className="btn bp-wr-equip"
                  disabled={equip.isPending}
                  onClick={() => doEquip(tryDefault ?? (tryOn!.slot as Slot), tryDefault ? null : tryOn!.id)}
                >
                  Надеть
                </button>
              ) : (
                <button
                  className="bp-wr-source"
                  onClick={() => {
                    haptics.tap();
                    navigate(tryOn?.from ? '/pass' : '/pass/decks');
                  }}
                >
                  {/* макет: две строки — «Ещё не получен» золотом + источник */}
                  <b>Ещё не получен</b>
                  <span>{tryOn ? fromLabel(tryOn) : ''} ›</span>
                </button>
              )}
            </div>
          </>
        ) : (
          <button
            className="btn bp-wr-others"
            onClick={() => {
              haptics.tap();
              navigate('/pass/wardrobe/preview');
            }}
          >
            <EyeIcon /> Как меня видят другие
          </button>
        )}
      </div>
    </div>
  );
}

function fromLabel(i: WItem): string {
  if (i.from) return `Ур. ${i.from.level}${i.from.track === 'free' ? '' : ' Pass+'}`;
  return 'Из колоды';
}

function EquippedTick() {
  return (
    <span className="bp-wr-tick" aria-hidden>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 12.5 9.5 18 20 6.5" />
      </svg>
    </span>
  );
}

/** Стандартный вариант слота (карточка «по умолчанию»). */
function DefaultThumb({ slot, name }: { slot: Slot; name: string }) {
  if (slot === 'frame') return <span className="bp-wr-framedemo bp-wr-framedemo--plain" />;
  if (slot === 'nick') return <span className="bp-wr-nickdemo">{name}</span>;
  if (slot === 'row') {
    return (
      <span className="bp-wr-rowdemo">
        <i className="dot" />
        <i className="line" />
      </span>
    );
  }
  return <span className="bp-wr-bgdefault" />;
}

/** Превью предмета в карточке 6.7 — как в макете (колоды.dc.html). */
function ItemThumb({ item, name }: { item: WItem; name: string }) {
  const c = { code: item.code, kind: item.kind, asset: item.asset, rarity: item.rarity };
  const url = assetUrl(item.asset);
  if (item.slot === 'frame' && url) {
    // Как в макете: рамка вокруг аватара-буквы, не пустое кольцо.
    return (
      <span className="bp-wr-framedemo">
        <CosmeticAvatar name={name} size={40} frame={c} />
      </span>
    );
  }
  if (item.slot === 'nick') {
    return (
      <span className="bp-wr-nickdemo">
        <NickFx cosmetic={c}>{name}</NickFx>
      </span>
    );
  }
  if (item.slot === 'row') {
    return (
      <span className={`bp-wr-rowdemo rfx-host${rowFxClass(c)}`}>
        <RowFxLayer cosmetic={c} height={26} />
        <i className="dot" />
        <i className="line" />
      </span>
    );
  }
  if (item.slot === 'bg') {
    if (item.kind === 'webp' && url) return <img className="bp-wr-bgfill" src={url} alt="" />;
    if (url) {
      return (
        <span className="bp-wr-bgfill bp-wr-bglottie">
          <LottieLayer src={url} mode="loop" width={120} height={62} />
        </span>
      );
    }
  }
  return <SlotGlyph slot={item.slot} size={26} />;
}
